import { useEffect, useState } from 'react';
import { send } from '@/shared/messages';
import type { Chapter } from '@/shared/types';
import { Select } from '@/components/Field';

// Doubles as the "View" entry point: choosing a chapter switches the panel
// into the chapter route. When already viewing a chapter, the select is
// styled "active" (blue ring) to mirror the highlighted-tab look.
export function ChapterPicker({
  activeChapterId,
  activeTestId,
  onChange,
  refreshKey,
  isViewing,
  compact = false,
}: {
  activeChapterId: string | null;
  // When set, only chapters in this test are listed.
  activeTestId: string | null;
  onChange: (id: string | null) => void;
  refreshKey: number;
  isViewing: boolean;
  // Narrow surfaces (mobile/side-panel top bar): render the design-system
  // Select sized to share a row with the compact TestPicker, instead of the
  // wider raw select used in the roomy wide header.
  compact?: boolean;
}) {
  const [chapters, setChapters] = useState<Chapter[]>([]);

  useEffect(() => {
    send({ type: 'LIST_CHAPTERS', testId: activeTestId ?? undefined }).then((r) =>
      setChapters(r.chapters)
    );
  }, [refreshKey, activeTestId]);

  if (compact) {
    if (chapters.length === 0) {
      return (
        <Select size="sm" disabled value="" className="w-full" title="No chapters in this test yet">
          <option value="">no chapters yet</option>
        </Select>
      );
    }
    return (
      <Select
        size="sm"
        value={activeChapterId ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className={`w-full${
          isViewing ? ' border-blue-600 font-medium text-blue-900 ring-1 ring-blue-600' : ''
        }`}
        title={isViewing ? 'Viewing chapter — pick another to switch' : 'Pick a chapter to view'}
      >
        <option value="">— chapter —</option>
        {chapters.map((c) => (
          <option key={c.id} value={c.id}>
            {c.title}
          </option>
        ))}
      </Select>
    );
  }

  if (chapters.length === 0) {
    return (
      <span className="rounded border border-dashed border-slate-300 px-2 py-1 text-[10px] text-slate-400">
        no chapters in this test yet
      </span>
    );
  }
  return (
    <select
      value={activeChapterId ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      className={`rounded border px-2 py-1 text-xs transition ${
        isViewing
          ? 'border-blue-600 bg-blue-50 font-medium text-blue-900 ring-1 ring-blue-600'
          : 'border-slate-300'
      }`}
      title={isViewing ? 'Viewing chapter — pick another to switch' : 'Pick a chapter to view'}
    >
      <option value="">— pick a chapter to view —</option>
      {chapters.map((c) => (
        <option key={c.id} value={c.id}>
          {c.title}
        </option>
      ))}
    </select>
  );
}
